import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLazyGetAdminSavedToursSessionsQuery } from "../../../../../app/services/book/admin_saved_tours"
import { formatDate } from "../../../admin_extension_tokens/utils/date"
import SessionDetailModal from "../SessionDetailModal"

const buildSessionUrl = (sessionKey) =>
  `${window.location.origin}/book/saved_tours/session/${sessionKey}`

const SessionsPanel = ({ drilldown, dateFrom, dateTo, onClose }) => {
  const { t } = useTranslation()
  const [fetchSessions, { currentData, isFetching }] = useLazyGetAdminSavedToursSessionsQuery()
  const [selectedSessionKey, setSelectedSessionKey] = useState(null)
  const [copiedKey, setCopiedKey] = useState(null)

  const params = useMemo(() => {
    if (!drilldown) return null
    const p = {}
    if (drilldown.type === "company") p.company_id = drilldown.id
    if (drilldown.type === "user") p.user_id = drilldown.id
    if (dateFrom) p.date_from = dateFrom
    if (dateTo) p.date_to = dateTo
    return p
  }, [drilldown, dateFrom, dateTo])

  useEffect(() => {
    if (params) fetchSessions(params)
  }, [params, fetchSessions])

  if (!drilldown) return null

  const sessions = currentData?.sessions || []
  const showLoading = isFetching || !currentData

  const handleOpen = (e, sessionKey) => {
    e.stopPropagation()
    window.open(buildSessionUrl(sessionKey), "_blank", "noopener,noreferrer")
  }

  const handleCopy = async (e, sessionKey) => {
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(buildSessionUrl(sessionKey))
      setCopiedKey(sessionKey)
      setTimeout(() => setCopiedKey((k) => (k === sessionKey ? null : k)), 1500)
    } catch {
      // clipboard unavailable
    }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-[90]" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full sm:w-[480px] bg-white z-[100] shadow-xl overflow-y-auto border-l border-[#E7EAEF]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E7EAEF] sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-base font-semibold text-[#1F2937]">{drilldown.name}</h2>
            <p className="text-xs text-[#9AA5B4]">{t("admin_saved_tours.panel.subtitle")}</p>
          </div>
          <button onClick={onClose} className="text-[#9AA5B4] hover:text-[#1F2937] text-lg leading-none">
            ✕
          </button>
        </div>

        {showLoading && (
          <div className="text-center py-16 text-[#9AA5B4]">{t("admin_saved_tours.panel.loading")}</div>
        )}

        {!showLoading && (
          <div className="px-5 py-4 flex flex-col gap-2">
            {sessions.map((s) => (
              <div
                key={s.session_key}
                className="rounded-xl border border-[#E7EAEF] hover:bg-[#F4F6F9] transition-colors overflow-hidden"
              >
                <button
                  onClick={() => setSelectedSessionKey(s.session_key)}
                  className="w-full text-left px-3 py-2.5"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#1F2937]">
                      {t("admin_saved_tours.panel.session_number", { number: s.session_number })}
                    </span>
                    <span className="text-xs text-[#9AA5B4]">{formatDate(s.created_at)}</span>
                  </div>
                  <div className="text-xs text-[#9AA5B4] mt-0.5">
                    {s.user?.name || s.user?.email}
                    {drilldown.type === "company" ? "" : ` · ${s.company_name}`}
                  </div>
                  <div className="text-xs text-[#9AA5B4] mt-0.5">
                    {t("admin_saved_tours.panel.tours_count", { count: s.tours_count })}
                    {s.temporary && ` · ${t("admin_saved_tours.panel.temporary_badge")}`}
                  </div>
                </button>

                <div className="flex items-center gap-2 px-3 pb-2.5 pt-1.5 border-t border-[#E7EAEF] bg-white">
                  <button
                    onClick={(e) => handleOpen(e, s.session_key)}
                    className="text-xs font-medium text-[#1F2937] px-2.5 py-1 rounded-lg border border-[#E7EAEF] hover:bg-[#F4F6F9] transition-colors"
                  >
                    {t("admin_saved_tours.panel.open")}
                  </button>
                  <button
                    onClick={(e) => handleCopy(e, s.session_key)}
                    className="text-xs font-medium text-[#1F2937] px-2.5 py-1 rounded-lg border border-[#E7EAEF] hover:bg-[#F4F6F9] transition-colors"
                  >
                    {copiedKey === s.session_key
                      ? t("admin_saved_tours.panel.copied")
                      : t("admin_saved_tours.panel.copy_link")}
                  </button>
                </div>
              </div>
            ))}

            {sessions.length === 0 && (
              <div className="text-center py-10 text-xs text-[#9AA5B4]">
                {t("admin_saved_tours.panel.no_sessions")}
              </div>
            )}
          </div>
        )}
      </div>

      <SessionDetailModal
        sessionKey={selectedSessionKey}
        onClose={() => setSelectedSessionKey(null)}
      />
    </>
  )
}

export default SessionsPanel
